import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Divider,
  Chip,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import { useCart } from '../contexts/CartContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

const CartPage: React.FC = () => {
  const { cartItems, removeFromCart, updateQuantity, clearCart, resetCart, getCartTotal } = useCart();
  const { mode } = useTheme();
  const navigate = useNavigate();
  const [cartError, setCartError] = useState<string | null>(null);

  // Handle cart errors
  useEffect(() => {
    try {
      // Test if cart functions work
      getCartTotal();
    } catch (error) {
      console.error('Cart error detected:', error);
      setCartError('There was an error loading your cart. Please try refreshing the page.');
    }
  }, [getCartTotal]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const handleQuantityChange = (cardId: string, newQuantity: number) => {
    try {
      updateQuantity(cardId, newQuantity);
    } catch (error) {
      console.error('Error updating quantity:', error);
      setCartError('Error updating item quantity. Please try again.');
    }
  };

  const handleRemoveItem = (cardId: string) => {
    try {
      removeFromCart(cardId);
    } catch (error) {
      console.error('Error removing item:', error);
      setCartError('Error removing item from cart. Please try again.');
    }
  };

  const handleCheckout = () => {
    try {
      navigate('/checkout');
    } catch (error) {
      console.error('Error navigating to checkout:', error);
      setCartError('Error proceeding to checkout. Please try again.');
    }
  };

  const handleContinueShopping = () => {
    try {
      navigate('/cards');
    } catch (error) {
      console.error('Error navigating to cards:', error);
    }
  };

  const handleClearCart = () => {
    try {
      clearCart();
    } catch (error) {
      console.error('Error clearing cart:', error);
      setCartError('Error clearing cart. Please try again.');
    }
  };

  const handleResetCart = () => {
    resetCart();
    setCartError(null);
  };

  const handleRefreshPage = () => {
    window.location.reload();
  };

  // Show error state if there's a cart error
  if (cartError) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <ShoppingCartIcon sx={{ fontSize: 80, color: 'error.main', mb: 3 }} />
          <Typography variant="h4" gutterBottom color="error">
            Cart Error
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            {cartError}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={handleRefreshPage}
              sx={{ px: 4, py: 1.5 }}
            >
              Refresh Page
            </Button>
            <Button
              variant="outlined"
              onClick={handleResetCart}
              sx={{ px: 4, py: 1.5 }}
            >
              Reset Cart
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  if (cartItems.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <ShoppingCartIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 3 }} />
          <Typography variant="h4" gutterBottom>
            Your cart is empty
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Looks like you haven't added any cards to your cart yet.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleContinueShopping}
            sx={{ px: 4, py: 1.5 }}
          >
            Start Shopping
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
        Shopping Cart
      </Typography>

      <Grid container spacing={4}>
        {/* Cart Items */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Cart Items ({cartItems.length})
            </Typography>
            
            {cartItems.map((item) => (
              <Box key={item.card.id}>
                <Card sx={{ mb: 2, display: 'flex' }}>
                  <CardMedia
                    component="img"
                    sx={{ width: 120, height: 120, objectFit: 'contain', p: 1 }}
                    image={item.card.imageUrl}
                    alt={item.card.title}
                  />
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {item.card.player}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {item.card.year} {item.card.brand} {item.card.team}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <Chip 
                          label={item.card.isGraded ? item.card.grade : item.card.condition} 
                          size="small" 
                          color={item.card.isGraded ? 'primary' : 'default'}
                        />
                        {item.card.isGraded && (
                          <Chip label="Graded" size="small" color="primary" variant="outlined" />
                        )}
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 700,
                        color: mode === 'light' ? '#000000' : '#FFFFFF'
                      }}>
                        {formatPrice(item.card.price)}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleQuantityChange(item.card.id, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <Typography variant="body1" sx={{ minWidth: 30, textAlign: 'center' }}>
                          {item.quantity}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleQuantityChange(item.card.id, item.quantity + 1)}
                        >
                          <AddIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveItem(item.card.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
                <Divider />
              </Box>
            ))}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                variant="outlined"
                color="error"
                onClick={handleClearCart}
              >
                Clear Cart
              </Button>
              <Button
                variant="outlined"
                onClick={handleContinueShopping}
              >
                Continue Shopping
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Order Summary */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: 'fit-content' }}>
            <Typography variant="h5" gutterBottom>
              Order Summary
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Subtotal:</Typography>
                <Typography variant="body1">{formatPrice(getCartTotal())}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Shipping:</Typography>
                <Typography variant="body1">Free</Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" fontWeight="bold">Total:</Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ 
                  color: mode === 'light' ? '#000000' : '#FFFFFF'
                }}>
                  {formatPrice(getCartTotal())}
                </Typography>
              </Box>
            </Box>
            
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleCheckout}
              sx={{ py: 1.5, mb: 2 }}
            >
              Proceed to Checkout
            </Button>
            
            <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
              Free shipping on orders over $50
            </Alert>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CartPage; 